/**
 * Centralised admin/partner-portal credential helpers.
 *
 * - Admin token: kept in localStorage under "alphalane_admin_token"
 * - Partner JWT: kept in localStorage under "alphalane_partner_token"
 * - Partner metadata (name/slug/etc): kept in "alphalane_partner_meta"
 *
 * The two modes are mutually exclusive: logging in as one clears the other.
 * Use `getAuthHeaders()` for every admin API call — it automatically returns
 * the right header.
 */

import axios from "axios";
import { BACKEND_URL } from "./apiBase";

const API = `${BACKEND_URL}/api`;
const ADMIN_KEY = "alphalane_admin_token";
const PARTNER_KEY = "alphalane_partner_token";
const PARTNER_META = "alphalane_partner_meta";

export function getAdminToken() {
  return typeof window !== "undefined" ? localStorage.getItem(ADMIN_KEY) : null;
}

export function getPartnerToken() {
  return typeof window !== "undefined" ? localStorage.getItem(PARTNER_KEY) : null;
}

export function getPartnerMeta() {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(PARTNER_META);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function setPartnerMeta(meta) {
  if (typeof window === "undefined") return;
  if (meta) localStorage.setItem(PARTNER_META, JSON.stringify(meta));
  else localStorage.removeItem(PARTNER_META);
}

export function isAuthed() {
  return !!getAdminToken() || !!getPartnerToken();
}

export function isPartner() {
  return !!getPartnerToken() && !getAdminToken();
}

export function isAdmin() {
  return !!getAdminToken();
}

export function logout() {
  if (typeof window === "undefined") return;
  const wasAdmin = !!getAdminToken();
  const wasPartner = !!getPartnerToken();
  localStorage.removeItem(ADMIN_KEY);
  localStorage.removeItem(PARTNER_KEY);
  localStorage.removeItem(PARTNER_META);
  // httpOnly-Session-Cookies serverseitig löschen (fire-and-forget).
  try {
    if (wasAdmin) axios.post(`${API}/admin/logout`, {}, { withCredentials: true }).catch(() => {});
    if (wasPartner) axios.post(`${API}/partner/logout`, {}, { withCredentials: true }).catch(() => {});
  } catch { /* noop */ }
}

/**
 * Returns the correct login URL based on the *current* (or previous) role.
 * When a partner is logged out via 401, we send them to /partner/login.
 * Admins go to /admin/login. Default: /admin/login.
 */
export function loginUrl() {
  if (typeof window === "undefined") return "/admin/login";
  // If we still have partner meta in storage, prefer partner-login
  if (localStorage.getItem(PARTNER_META) || localStorage.getItem(PARTNER_KEY)) {
    return "/partner/login";
  }
  return "/admin/login";
}

/**
 * Returns the auth headers to send with admin/partner API calls.
 *
 * Seit der httpOnly-Cookie-Migration liegt das echte Token im Session-Cookie;
 * localStorage hält nur den Marker "1". Für Alt-Sessions (echtes Token noch
 * in localStorage) werden die Legacy-Header weiterhin mitgeschickt — das
 * Backend akzeptiert beides (Cookie hat Vorrang).
 */
export function getAuthHeaders() {
  const headers = {};
  const at = getAdminToken();
  const pt = getPartnerToken();
  if (at && at !== "1") headers["X-Admin-Token"] = at;
  if (pt && pt !== "1") headers["Authorization"] = `Bearer ${pt}`;
  return headers;
}
